import React, { useEffect, useContext } from "react";
import clsx from "clsx";
import { Row, Col } from "react-bootstrap";
import Typography from "@mui/material/Typography";
import { AuthContext } from "./contexts/AuthContext";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { MessageToast, ToastMode } from "./shared/MessageToast";
import { Customer } from "./shared/interfaces/Customer";
import { LOCALSTORAGE_KEYS } from "./shared/constants";
import { useCustomers } from "./data/useCustomers";
import { ValidationMessage } from "./shared/ValidationMessage";
import { ChangeHandler } from "react-hook-form";
import { Autocomplete, TextField } from "@mui/material";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

interface CustomerDropdownProps {
  selectedCustomer?: Customer | undefined;
  onCustomerSelect: (customer?: Customer) => void;
  invalid?: boolean;
  invalidMessage?: string;
  onSingleCustomerAvailable?: (customer: Customer) => void;
  displaySingleCustomerName?: boolean;
  showLabel?: boolean;
  onChange?: ChangeHandler;
  updateLocalStorage?: boolean;
  customerId?: number;
  className?: string;
  disabled?: boolean;
  filterUserManagement?: boolean;
}

const customerStorageKey = LOCALSTORAGE_KEYS.customer;
const hvSubstationSortFieldStorageKey = LOCALSTORAGE_KEYS.hvSubstationSortField;
const hvSubstationSortOrderStorageKey = LOCALSTORAGE_KEYS.hvSubstationSortOrder;
const workflowsSortFieldStorageKey = LOCALSTORAGE_KEYS.workflowsSortField;
const workflowsSortOrderStorageKey = LOCALSTORAGE_KEYS.workflowsSortOrder;
const workOrdersSortFieldStorageKey = LOCALSTORAGE_KEYS.workOrdersSortField;
const workOrdersSortOrderStorageKey = LOCALSTORAGE_KEYS.workOrdersSortOrder;
const resultsSortFieldStorageKey = LOCALSTORAGE_KEYS.resultsSortField;
const resultsSortOrderStorageKey = LOCALSTORAGE_KEYS.resultsSortOrder;
const companiesSortFieldStorageKey = LOCALSTORAGE_KEYS.companiesSortField;
const companiesSortOrderStorageKey = LOCALSTORAGE_KEYS.companiesSortOrder;
const sitesSortFieldStorageKey = LOCALSTORAGE_KEYS.sitesSortField;
const sitesSortOrderStorageKey = LOCALSTORAGE_KEYS.sitesSortOrder;

export const CustomerDropdown = React.forwardRef<typeof Autocomplete, CustomerDropdownProps>((props, ref) => {
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const { refetch: refetchCustomers, ...customers } = useCustomers();

  const {
    selectedCustomer,
    onCustomerSelect,
    invalid,
    invalidMessage,
    onSingleCustomerAvailable,
    displaySingleCustomerName,
    showLabel,
    updateLocalStorage = true,
    disabled,
    filterUserManagement = false,
  } = props;

  const { canI } = authContext;

  let customerOptions = customers.data;

  if (filterUserManagement) {
    customerOptions = customers.data?.filter((customer: Customer) => canI("edit", "customer", customer.id));
  }

  useEffect(() => {
    if (authContext.userIsAuthenticated) {
      refetchCustomers();
    }
  }, [authContext.userIsAuthenticated, refetchCustomers]);

  useEffect(() => {
    if (!updateLocalStorage) {
      return;
    }
    const customerId = localStorage.getItem(customerStorageKey);
    const storedCustomer = customers.data?.find((customer: Customer) => customer.id.toString() === customerId);
    if (storedCustomer && selectedCustomer !== storedCustomer) {
      storedCustomer && onCustomerSelect(storedCustomer);
    }
  }, [customers.data, selectedCustomer, onCustomerSelect, updateLocalStorage]);

  useEffect(() => {
    if (customers.data && customers.data.length === 1 && onSingleCustomerAvailable) {
      onSingleCustomerAvailable(customers.data[0]);
      onCustomerSelect(customers.data[0]);
    }
  }, [customers.data, onSingleCustomerAvailable, onCustomerSelect]);

  function handleChange(e: React.SyntheticEvent, value: Customer | null) {
    if (value == null) {
      return;
    }
    if (updateLocalStorage) {
      localStorage.setItem(customerStorageKey, value.id.toString());
      localStorage.removeItem(LOCALSTORAGE_KEYS.hvSubstation);
      localStorage.removeItem(hvSubstationSortFieldStorageKey);
      localStorage.removeItem(hvSubstationSortOrderStorageKey);
      localStorage.removeItem(workflowsSortFieldStorageKey);
      localStorage.removeItem(workflowsSortOrderStorageKey);
      localStorage.removeItem(workOrdersSortFieldStorageKey);
      localStorage.removeItem(workOrdersSortOrderStorageKey);
      localStorage.removeItem(resultsSortFieldStorageKey);
      localStorage.removeItem(resultsSortOrderStorageKey);
      localStorage.removeItem(companiesSortFieldStorageKey);
      localStorage.removeItem(companiesSortOrderStorageKey);
      localStorage.removeItem(sitesSortFieldStorageKey);
      localStorage.removeItem(sitesSortOrderStorageKey);
    }

    onCustomerSelect(value);
  }

  const customerLabelTemplate = (
    <Col xs={"auto"} className="d-flex align-items-center">
      <Typography variant="subtitle2" color="textPrimary">
        {t("customer.customer")}:{" "}
      </Typography>
    </Col>
  );

  if (customers.isLoading) {
    return <CircularProgress size={24} />;
  } else if (customers.error) {
    return (
      <MessageToast text={t("common.dataFetchError", { val: "$t(customer.customers)" })} mode={ToastMode.Error} show />
    );
  } else if (customers.data?.length === 1) {
    return displaySingleCustomerName ? (
      <Row className="d-flex flex-column">
        {showLabel && customerLabelTemplate}
        <Col xs={"auto"}>{customers.data && customers.data?.length === 1 ? customers.data[0].name : null}</Col>
      </Row>
    ) : null;
  } else if (customers.data && customers.data.length > 1)
    return (
      <Row className="d-flex flex-column">
        {showLabel && customerLabelTemplate}
        <Col xs={"auto"}>
          {customers.data && customers.data?.length === 1 && displaySingleCustomerName ? (
            customers.data[0].name
          ) : customers.data && customers.data?.length === 1 ? null : (
            <Autocomplete
              ref={ref}
              className={clsx({ "p-invalid": invalid })}
              disableClearable={selectedCustomer != null}
              autoHighlight
              blurOnSelect
              id="customerDropdown"
              value={selectedCustomer ?? null}
              options={filterUserManagement && customerOptions ? customerOptions : customers.data}
              disabled={disabled}
              renderInput={(params) => <TextField {...params} placeholder={t("customer.selectCustomer")} />}
              renderOption={(props, option, { inputValue }) => {
                const matches = match(option.name, inputValue, {
                  insideWords: true,
                });
                const parts = parse(option.name, matches);
                return (
                  <li {...props}>
                    <div>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </li>
                );
              }}
              size="small"
              sx={{ width: 250, bgcolor: "white" }}
              onChange={handleChange}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}
          {invalid && invalidMessage && (
            <ValidationMessage invalidMessage={invalidMessage} validationType="error" showIcon />
          )}
        </Col>
      </Row>
    );
  return null;
});
